import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const dt = (v) => (v ? new Date(v).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '—');

const STATI = {
  aggiornato: { label: 'Aggiornato', cls: 'text-emerald-400 border-emerald-400/40 bg-emerald-400/10', Icon: CheckCircle2 },
  da_aggiornare: { label: 'Da aggiornare', cls: 'text-amber-400 border-amber-400/40 bg-amber-400/10', Icon: AlertTriangle },
  errore: { label: 'Errore', cls: 'text-red-400 border-red-400/40 bg-red-400/10', Icon: XCircle },
};

const Badge = ({ stato }) => {
  const s = STATI[stato] || STATI.errore;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded border text-xs font-bold whitespace-nowrap ${s.cls}`}>
      <s.Icon size={13} /> {s.label}
    </span>
  );
};

export const PrerenderStatusPanel = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/prerender-status`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) {
      toast.error('Errore nel caricamento dello stato istantanee');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  if (loading && !data) return <p className="text-white/50" data-testid="prerender-loading">Controllo il sito pubblicato…</p>;
  if (!data) return null;

  return (
    <div data-testid="prerender-panel">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="font-heading text-xl text-white mb-1">ISTANTANEE HTML (SEO)</h2>
          <p className="text-white/50 text-sm max-w-2xl">
            Controlla il sito pubblicato come lo vedono Google e i crawler IA, cioè senza JavaScript, e confronta
            il testo con quello salvato nel pannello. Le istantanee si rigenerano da sole a ogni pubblicazione:
            "Da aggiornare" significa che online c'è ancora una versione precedente.
          </p>
        </div>
        <button
          onClick={fetchStatus}
          disabled={loading}
          className="flex items-center gap-2 bg-white/10 border border-gold/40 text-gold hover:bg-gold hover:text-black px-4 py-2 rounded font-bold text-sm transition-all disabled:opacity-50"
          data-testid="prerender-refresh-btn"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Ricontrolla
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          ['URL controllati', data.totali, 'text-white'],
          ['Da aggiornare', data.da_aggiornare, data.da_aggiornare ? 'text-amber-400' : 'text-white'],
          ['Errori', data.errori, data.errori ? 'text-red-400' : 'text-white'],
          ['Ultima rigenerazione', dt(data.generata_il), 'text-white text-base'],
        ].map(([label, val, cls]) => (
          <div key={label} className="bg-white/5 border border-white/10 rounded p-4">
            <p className="text-white/40 text-xs uppercase tracking-wide mb-1">{label}</p>
            <p className={`font-heading text-2xl ${cls}`}>{val ?? '—'}</p>
          </div>
        ))}
      </div>

      {data.sorgenti_piu_recenti && (
        <div className="flex items-start gap-3 bg-amber-400/10 border border-amber-400/30 rounded p-4 mb-6" data-testid="prerender-warning">
          <AlertTriangle size={18} className="text-amber-400 mt-0.5 shrink-0" />
          <p className="text-amber-200/90 text-sm">
            Le pagine del sito sono state modificate dopo l'ultima rigenerazione delle istantanee
            ({dt(data.sorgenti_modificati_il)}): serve una nuova pubblicazione perché i crawler vedano i testi aggiornati.
          </p>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="prerender-table">
          <thead>
            <tr className="text-white/40 text-xs uppercase tracking-wide border-b border-white/10">
              <th className="text-left py-3 pr-4">URL</th>
              <th className="text-left py-3 pr-4">Tipo</th>
              <th className="text-left py-3 pr-4">Stato</th>
              <th className="text-right py-3 pr-4">Testo letto</th>
              <th className="text-left py-3">Nota</th>
            </tr>
          </thead>
          <tbody>
            {(data.righe || []).map((r) => (
              <tr key={`${r.tipo}-${r.url}`} className="border-b border-white/5 text-white/80">
                <td className="py-3 pr-4 font-mono text-xs">{r.url}</td>
                <td className="py-3 pr-4 text-white/50 whitespace-nowrap">{r.tipo}</td>
                <td className="py-3 pr-4"><Badge stato={r.stato} /></td>
                <td className="py-3 pr-4 text-right text-white/60">{r.testo ? `${r.testo.toLocaleString('it-IT')} car.` : '—'}</td>
                <td className="py-3 text-white/50 text-xs">{r.nota || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
